/**
 * AddEditSourceDialog Component
 * Modal for creating or editing training sources
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  useCreateSource, 
  useUpdateSource, 
  type TrainingSource 
} from '@/hooks/useDiscoverySources';
import { useProvidersExtended } from '@/hooks/useProviderManagement';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_TYPES = [
  { value: 'youtube_channel', label: 'YouTube Channel' },
  { value: 'youtube_playlist', label: 'YouTube Playlist' },
  { value: 'google_books', label: 'Google Books' },
  { value: 'rss', label: 'RSS Feed' },
  { value: 'calendar', label: 'Calendar' },
];

interface AddEditSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: TrainingSource | null; // If provided, edit mode
  defaultProviderId?: string;
}

const STORAGE_KEY = 'add-source-dialog-form';

interface StoredFormState {
  providerId: string;
  sourceType: string;
  sourceIdentifier: string;
  sourceName: string;
  ttlDays: number;
  maxItemsPerFetch: number;
}

function getStoredFormState(): StoredFormState | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function clearStoredFormState() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function AddEditSourceDialog({
  open,
  onOpenChange,
  source,
  defaultProviderId,
}: AddEditSourceDialogProps) {
  const isEditMode = !!source;
  
  // Initialize from sessionStorage for add mode
  const storedState = !isEditMode ? getStoredFormState() : null;
  
  const [providerId, setProviderId] = useState(storedState?.providerId || '');
  const [sourceType, setSourceType] = useState(storedState?.sourceType || 'youtube_channel');
  const [sourceIdentifier, setSourceIdentifier] = useState(storedState?.sourceIdentifier || '');
  const [sourceName, setSourceName] = useState(storedState?.sourceName || '');
  const [ttlDays, setTtlDays] = useState(storedState?.ttlDays || 7);
  const [maxItemsPerFetch, setMaxItemsPerFetch] = useState(storedState?.maxItemsPerFetch || 50);

  const { data: providers } = useProvidersExtended();
  const createSource = useCreateSource();
  const updateSource = useUpdateSource();

  // Persist form state to sessionStorage for add mode
  useEffect(() => {
    if (!isEditMode && open) {
      const formState: StoredFormState = {
        providerId,
        sourceType,
        sourceIdentifier,
        sourceName,
        ttlDays,
        maxItemsPerFetch,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formState));
    }
  }, [isEditMode, open, providerId, sourceType, sourceIdentifier, sourceName, ttlDays, maxItemsPerFetch]);

  // Reset form when dialog opens/closes or source changes
  useEffect(() => {
    if (open && source) {
      setProviderId(source.provider_id);
      setSourceType(source.source_type);
      setSourceIdentifier(source.source_identifier);
      setSourceName(source.source_name);
      setTtlDays(source.ttl_days || 7);
      setMaxItemsPerFetch(source.max_items_per_fetch || 50);
    } else if (open && !source) {
      // For add mode, only reset if there's no stored state
      const stored = getStoredFormState();
      if (!stored) {
        setProviderId(defaultProviderId || '');
        setSourceType('youtube_channel');
        setSourceIdentifier('');
        setSourceName('');
        setTtlDays(7);
        setMaxItemsPerFetch(50);
      }
    }
  }, [open, source, defaultProviderId]);

  const handleSubmit = async () => {
    if (!providerId) {
      toast.error('Please select a provider');
      return;
    }
    if (!sourceIdentifier.trim()) {
      toast.error('Source identifier is required');
      return;
    }
    if (!sourceName.trim()) {
      toast.error('Source name is required');
      return;
    }

    try {
      if (isEditMode && source) {
        await updateSource.mutateAsync({
          sourceId: source.id,
          updates: {
            source_type: sourceType,
            source_identifier: sourceIdentifier.trim(),
            source_name: sourceName.trim(),
            ttl_days: ttlDays,
            max_items_per_fetch: maxItemsPerFetch,
          },
        });
        toast.success('Source updated');
      } else {
        await createSource.mutateAsync({
          provider_id: providerId,
          source_type: sourceType,
          source_identifier: sourceIdentifier.trim(),
          source_name: sourceName.trim(),
          ttl_days: ttlDays,
          max_items_per_fetch: maxItemsPerFetch,
        });
        toast.success('Source created');
        clearStoredFormState();
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save source');
    }
  };

  const getPlaceholder = () => {
    switch (sourceType) {
      case 'youtube_channel':
        return 'Channel ID (e.g., UCxxxxxxx)';
      case 'youtube_playlist':
        return 'Playlist ID (e.g., PLxxxxxxx)';
      case 'google_books':
        return 'Search query (e.g., automotive diagnostics)';
      case 'rss':
        return 'RSS feed URL';
      case 'calendar':
        return 'Calendar URL or ID';
      default:
        return 'Identifier';
    }
  };

  const isSubmitting = createSource.isPending || updateSource.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Source' : 'Add Source'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update source configuration.'
              : 'Add a new discovery source for content ingestion.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="provider">Provider *</Label>
            <Select 
              value={providerId} 
              onValueChange={setProviderId}
              disabled={isEditMode}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a provider" />
              </SelectTrigger>
              <SelectContent>
                {providers?.filter(p => p.is_active).map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {provider.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="sourceType">Source Type *</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="sourceIdentifier">Source Identifier *</Label>
            <Input
              id="sourceIdentifier"
              value={sourceIdentifier}
              onChange={(e) => setSourceIdentifier(e.target.value)}
              placeholder={getPlaceholder()}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="sourceName">Friendly Name *</Label>
            <Input
              id="sourceName"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              placeholder="e.g., Scanner Danner Videos"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="ttlDays">TTL (days)</Label>
              <Input
                id="ttlDays"
                type="number"
                min={1}
                max={365}
                value={ttlDays}
                onChange={(e) => setTtlDays(parseInt(e.target.value) || 7)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="maxItems">Max Items per Fetch</Label>
              <Input
                id="maxItems"
                type="number"
                min={1}
                max={100}
                value={maxItemsPerFetch}
                onChange={(e) => setMaxItemsPerFetch(parseInt(e.target.value) || 50)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Source'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditSourceDialog;
